import React, { useCallback, useMemo } from 'react';

import {
  Button,
  Center,
  Flex,
  Grid,
  GridItem,
  GridProps,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Project } from '../models/Project';
import { TextType } from '../models/Text';

import Spacer from './Spacer';
import SquareCard from './SquareCard';
import Text from './Text';

interface ProjectsGalleryProps {
  items: Project[];
  gap?: GridProps['gap'];
  buttonLabel?: string;
  onClick?: () => void;
}

const ProjectsGallery = ({
  items,
  gap = {
    base: '12px',
    md: '12px',
    lg: '12px',
  },
  buttonLabel,
  onClick = () => null,
}: ProjectsGalleryProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const itemsToDisplay = useMemo<Project[]>(
    () => items.slice(0, 6),
    [items]
  );

  const handleProjectClick = useCallback(
    (id: number) => {
      navigate(`/projects/${id}`);
    },
    [navigate]
  );

  return (
    <>
      <Grid
        width="100%"
        templateColumns={{
          base: 'repeat(1, 1fr)',
          md: 'repeat(2, 1fr)',
          lg: 'repeat(3, 1fr)',
        }}
        gap={gap}
        alignContent="start"
      >
        {itemsToDisplay.map((project, index) => (
          <GridItem
            key={`item-${project.title}-${index}`}
            width="100%"
            aspectRatio={1}
            overflow="hidden"
          >
            <button
              type="button"
              onClick={() =>
                handleProjectClick(project.id)
              }
              style={{
                display: 'block',
                width: '100%',
                height: '100%',
                padding: 0,
                margin: 0,
                border: 0,
                background: 'transparent',
                cursor: 'pointer',
              }}
            >
              <SquareCard
                heroImage={project.heroImage}
                withShadow
                visibleNode={
                  <Flex
                    width="100%"
                    height="100%"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="flex-end"
                    paddingX={{
                      base: '20px',
                      md: '30px',
                      lg: '40px',
                    }}
                    paddingBottom="40px"
                    boxSizing="border-box"
                  >
                    <Text
                      type={TextType.Inter20Regular}
                      color="white"
                      others={{
                        textAlign: 'center',
                        width: '100%',
                      }}
                    >
                      {project.title.toUpperCase()}
                    </Text>
                  </Flex>
                }
              >
                <Flex
                  width="100%"
                  height="100%"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="flex-end"
                  paddingX={{
                    base: '20px',
                    md: '30px',
                    lg: '40px',
                  }}
                  paddingBottom="40px"
                  boxSizing="border-box"
                >
                  <Text
                    type={TextType.Inter20Regular}
                    color="white"
                    others={{
                      textAlign: 'center',
                      width: '100%',
                    }}
                  >
                    {project.title.toUpperCase()}
                  </Text>

                  <Spacer size={12} />

                  <Text
                    type={{
                      base: TextType.Inter12Light,
                      md: TextType.Inter14Light,
                    }}
                    color="white"
                    others={{
                      textAlign: 'center',
                      width: '100%',
                    }}
                  >
                    {project.shortDescription}
                  </Text>
                </Flex>
              </SquareCard>
            </button>
          </GridItem>
        ))}
      </Grid>

      <Spacer size={30} />

      <Center width="100%">
        <Button
          variant="solid"
          bgColor="primary"
          width={{
            base: '50%',
            md: '33.3333%',
          }}
          borderRadius={0}
          onClick={onClick}
        >
          <Text
            type={{
              base: TextType.Inter12Bold,
              md: TextType.Inter14Bold,
            }}
            color="white"
          >
            {(
              buttonLabel ||
              t('homepage.projects.buttonLabel')
            ).toUpperCase()}
          </Text>
        </Button>
      </Center>
    </>
  );
};

export default ProjectsGallery;